import { useEffect, useMemo, useRef } from "react";
import { usePersistentChatStore } from "src/stores/persistentChatStore";
import { getAgenticChatNeedsUserAttention } from "./agenticTabAttention";
import { buildAttentionFaviconDataUrlFromHref } from "./tabAttentionFavicon";
import { useCurrentChat } from "./PersistentChatQueries";
import { NEW_CHAT_ID } from "./PersistentChatTypes";

type Params = {
  projectId: string;
  currentChatId: string;
};

export { getAgenticChatNeedsUserAttention } from "./agenticTabAttention";

function findShortcutIconElement(): HTMLLinkElement | null {
  const withSizes = document.querySelector('link[rel~="icon"][sizes="32x32"]') as HTMLLinkElement | null;
  if (withSizes) return withSizes;
  const shortcut = document.querySelector('link[rel="shortcut icon"]') as HTMLLinkElement | null;
  if (shortcut) return shortcut;
  return document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
}

/**
 * Composites the current tab favicon with a small purple checkmark badge (top-right) while the tab is in
 * the background and agentic chat needs the user. Restores the href that was current when the composite was
 * applied (or the pre-load snapshot) when the tab is focused or attention is cleared.
 */
export function useAgenticTabAttentionFavicon({ projectId, currentChatId }: Params): void {
  const effectiveChatId = currentChatId ?? NEW_CHAT_ID;
  const { data: chatData } = useCurrentChat(projectId, effectiveChatId);
  const messages = chatData?.messages;
  const feedbackByMessageId = chatData?.feedbackByMessageId;

  const streaming = usePersistentChatStore((s) => s.streaming);
  const pendingAgentResponse = usePersistentChatStore((s) => s.pendingAgentResponse);
  const activePlan = usePersistentChatStore((s) => s.activePlan);
  const suggestedAction = usePersistentChatStore((s) => s.suggestedAction);
  const currentUserQuestion = usePersistentChatStore((s) => s.currentUserQuestion);
  const ctaAction = usePersistentChatStore((s) => s.ctaAction);

  const needsAttention = useMemo(
    () =>
      getAgenticChatNeedsUserAttention(
        { streaming, pendingAgentResponse, activePlan, suggestedAction, currentUserQuestion, ctaAction },
        messages,
        feedbackByMessageId,
      ),
    [
      streaming,
      pendingAgentResponse,
      activePlan,
      suggestedAction,
      currentUserQuestion,
      ctaAction,
      messages,
      feedbackByMessageId,
    ],
  );

  /** Href to restore (same-origin / path as when we applied the badge). */
  const storedHrefRef = useRef<string | null>(null);
  const storedRelRef = useRef<string | null>(null);
  const storedTypeRef = useRef<string | null>(null);
  const isShowingRef = useRef(false);
  const applyTokenRef = useRef(0);
  const loadTokenRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const restore = () => {
      if (!isShowingRef.current || storedHrefRef.current === null) return;
      const el = findShortcutIconElement();
      if (el) {
        el.href = storedHrefRef.current;
        if (storedRelRef.current !== null) el.rel = storedRelRef.current;
        if (storedTypeRef.current !== null) el.type = storedTypeRef.current;
      }
      storedHrefRef.current = null;
      storedRelRef.current = null;
      storedTypeRef.current = null;
      isShowingRef.current = false;
    };

    const applyCompositeForHiddenTab = () => {
      if (cancelled || !needsAttention || document.visibilityState !== "hidden") return;

      const el = findShortcutIconElement();
      if (!el) return;

      if (isShowingRef.current && (el.href.startsWith("data:image/") || el.href.includes("/assets/favicon-attention."))) {
        return;
      }

      const baseHref = el.href;
      const myLoad = ++loadTokenRef.current;

      void (async () => {
        const attentionHref = await buildAttentionFaviconDataUrlFromHref(baseHref);
        if (cancelled || myLoad !== loadTokenRef.current) return;
        if (!needsAttention || document.visibilityState !== "hidden") return;

        const link = findShortcutIconElement();
        if (!link) return;

        if (!isShowingRef.current) {
          storedHrefRef.current = link.href;
          storedRelRef.current = link.rel || null;
          storedTypeRef.current = link.type || null;
        }
        isShowingRef.current = true;
        if (link.href !== attentionHref) {
          link.rel = "icon";
          link.type = attentionHref.endsWith(".png") ? "image/png" : "image/svg+xml";
          link.href = attentionHref;
        }
      })();
    };

    if (!needsAttention) {
      applyTokenRef.current += 1;
      loadTokenRef.current += 1;
      restore();
      return () => {
        cancelled = true;
        applyTokenRef.current += 1;
        loadTokenRef.current += 1;
        restore();
      };
    }

    const token = ++applyTokenRef.current;

    const onVis = () => {
      if (token !== applyTokenRef.current || cancelled) return;
      if (document.visibilityState === "visible") {
        restore();
      } else {
        applyCompositeForHiddenTab();
      }
    };

    applyCompositeForHiddenTab();
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      applyTokenRef.current += 1;
      loadTokenRef.current += 1;
      document.removeEventListener("visibilitychange", onVis);
      restore();
    };
  }, [needsAttention]);
}
